import React, { ReactNode, createContext, useContext, useState } from "react";

import { LOCAL_STORAGE_KEY } from "../constants";
import { getMe } from "../services/api/commonApi";
import { getToken } from "../utils/common/common";

export interface User {
  affiliate_user_code: number;
  affiliate_code: number;
  shop_code: number;
  username: string;
  is_admin: boolean;
  status: number;
  created_u_code: number;
  updated_u_code: number;
  pref?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  fetchUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);

  // Function to fetch user from API
  const fetchUser = async () => {
    const token = getToken();
    if (!token) {
      setUser(null);
      return;
    }

    try {
      const userData = await getMe();
      setUser(userData);
    } catch (error) {
      console.error("Failed to fetch user:", error);
      setUser(null);
      // If token is invalid, remove token
      localStorage.removeItem(LOCAL_STORAGE_KEY.ACCESS_TOKEN);
    }
  };

  const value = {
    user,
    isAuthenticated: !!user,
    fetchUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
